/// <amd-module name="Repos/Views/Settings/Repositories/Components/RepositoriesTable" />
import React = require("react");
import { CheckIssueType } from "Checks/Client/Contracts/PipelinesChecks";
import { GitRepositoryPermissions } from "Repos/Common/Permissions";
import { ISettingsViewService } from "Repos/Views/Settings/Common/Services/SettingsViewService";
import { DialogType, IRepository } from "Repos/Views/Settings/Repositories/Contracts/Contracts";
import * as Resources from "Repos/Views/Settings/Repositories/Resources";
import { IRepositoriesViewService } from "Repos/Views/Settings/Repositories/Services/RepositoriesViewService";
import { IObservableArray, IReadonlyObservableArray, IReadonlyObservableValue, ObservableArray, ObservableObject } from "VSS/Core/Observable";
import { IVssContributionService } from "VSS/Platform/Contribution";
import { onClickFPS } from "VSS/Platform/FPS";
import { IVssComponentContext, RegionComponent, VssComponent, css } from "VSS/Platform/Layout";
import { ExpandableButton } from "VSSUI/Button";
import { Card } from "VSSUI/Card";
import { Icon } from "VSSUI/Icon";
import { IMenuItem, IMenuProps } from "VSSUI/Menu";
import { Observer } from "VSSUI/Observer";
import { Spinner, SpinnerSize } from "VSSUI/Spinner";
import { ColumnMore, ITableColumn, SimpleTableCell, Table, TableColumnLayout, TableRow, renderColumns } from "VSSUI/Table";

export class RepositoriesTable extends VssComponent {
    private columns: ITableColumn<{}>[];
    private data: IReadonlyObservableArray<IRepository | IReadonlyObservableValue<IRepository | undefined>>;
    private settingsSvc: ISettingsViewService;
    private svc: IRepositoriesViewService;
    private readonly contributionService: IVssContributionService;
    private repositoryIdToAdvSecEnablementStatus = new ObservableObject<boolean>();

    public constructor(props: {}, context: IVssComponentContext) {
        super(props, context);

        this.svc = context.pageContext.getService<IRepositoriesViewService>("RepositoriesViewService");
        this.settingsSvc = context.pageContext.getService<ISettingsViewService>("SettingsViewService");
        this.data = this.svc.getFilteredRepositories();
        this.columns = this.getColumns();
        this.contributionService = context.pageContext.getService<IVssContributionService>("IVssContributionService");

        this.contributionService
            .getDataAsync(
                "ms.vss-advsec.advanced-security-project-enablement-data-provider",
                {
                    givenProjectId: this.svc.getProject().id
                },
                true
            )
            .then((projectEnablementStatuses: { repositoryId: string; advSecEnabled: boolean }[]) => {
                projectEnablementStatuses?.forEach(status => {
                    this.repositoryIdToAdvSecEnablementStatus.set(status.repositoryId, status.advSecEnabled);
                });
            });
    }

    public render = (): JSX.Element => {
        return (
            <Card className="flex-grow bolt-card-no-vertical-padding" contentProps={{ contentPadding: false }}>
                <Table<IRepository>
                    ariaLabel={Resources.Repositories}
                    columns={this.columns}
                    itemProvider={this.data}
                    role="table"
                    showHeader={false}
                    renderRow={(rowIndex, item, rowDetails) => {
                        return (
                            <Observer repositoryIdToAdvSecEnablementStatus={this.repositoryIdToAdvSecEnablementStatus}>
                                {() => (
                                    <TableRow
                                        details={rowDetails}
                                        index={rowIndex}
                                        linkProps={{
                                            href: this.settingsSvc.getUrl(item.id, undefined, undefined),
                                            onClick: event => this.onClick(event, item)
                                        }}
                                    >
                                        {renderColumns(rowIndex, this.getColumns(), item, rowDetails)}
                                    </TableRow>
                                )}
                            </Observer>
                        );
                    }}
                />
            </Card>
        );
    };

    private getColumns = (): ITableColumn<{}>[] => {
        return [
            {
                columnLayout: TableColumnLayout.singleLinePrefix,
                id: "name",
                name: Resources.NameCol,
                readonly: true,
                renderCell: (rowIndex: number, columnIndex: number, tableColumn: ITableColumn<IRepository>, tableItem: IRepository) => {
                    return (
                        <SimpleTableCell columnIndex={columnIndex} key={tableItem.id} tableColumn={tableColumn}>
                            <Icon
                                {...tableItem.iconProps}
                                className={css("padding-horizontal-8 padding-vertical-4 font-size-ml", tableItem.iconProps.className)}
                            />
                            <span className="text-ellipsis flex-self-center">{tableItem.name}</span>
                            <RegionComponent
                                componentRegion="ms.vss-code-web.advanced-security-repositories-table-extension"
                                childComponentProperties={{
                                    advSecEnabled: this.repositoryIdToAdvSecEnablementStatus.get(tableItem.id)
                                }}
                            />
                            {tableItem.issue && (
                                <Icon
                                    className={tableItem.issue.type === CheckIssueType.Error ? "resource-error-icon" : "resource-warning-icon"}
                                    iconName={tableItem.issue.type === CheckIssueType.Error ? "ErrorBadge" : "Warning"}
                                    tooltipProps={{
                                        text: tableItem.issue.message
                                    }}
                                />
                            )}
                        </SimpleTableCell>
                    );
                },
                width: -100
            },
            new ColumnMore(this.getMoreMenuItems, (item: IRepository) => {
                return !item.isTfvc;
            })
        ];
    };

    private getMoreMenuItems = (item: IRepository, rowIndex: number, buttonRef: React.RefObject<ExpandableButton>): IMenuProps => {
        const itemArr: IObservableArray<IMenuItem> = new ObservableArray([
            {
                id: "goto",
                text: Resources.GoTo,
                data: item,
                onActivate: this.onGoTo,
                href: item.webUrl,
                iconProps: {
                    iconName: "View"
                }
            }
        ]);

        const renameItem: IMenuItem = {
            id: "rename",
            text: Resources.Rename,
            data: item,
            onActivate: menuItem => this.onRenameRow(menuItem, () => buttonRef.current?.focus()),
            iconProps: {
                iconName: "Rename",
                render: renderLoadingIcon
            },
            disabled: true
        };

        itemArr.push(renameItem);

        this.svc.checkPermissionsAsync(GitRepositoryPermissions.RenameRepository, item.id).then(hasPermission => {
            if (hasPermission) {
                renameItem.disabled = false;
            }

            renameItem.iconProps!.render = undefined;
            itemArr.value = itemArr.value.slice();
        });

        const deleteItem: IMenuItem = {
            id: "delete",
            text: Resources.Delete,
            data: item,
            onActivate: menuItem => this.onDeleteRow(menuItem, () => buttonRef.current?.focus()),
            iconProps: {
                iconName: "Delete",
                render: renderLoadingIcon
            },
            disabled: true
        };

        itemArr.push(deleteItem);

        this.svc.checkPermissionsAsync(GitRepositoryPermissions.DeleteRepository, item.id).then(hasPermission => {
            if (hasPermission) {
                deleteItem.disabled = false;
            }

            deleteItem.iconProps!.render = undefined;
            itemArr.value = itemArr.value.slice();
        });

        const menuProps: IMenuProps = {
            id: "sub-menu",
            items: itemArr
        };

        return menuProps;
    };

    private onClick = (
        event: React.MouseEvent<HTMLAnchorElement, MouseEvent> | React.KeyboardEvent<HTMLAnchorElement> | undefined,
        item: IRepository
    ) => {
        if ((!event || (!event.shiftKey && !event.ctrlKey)) && event ? !event.defaultPrevented : true) {
            this.svc.setSelected(item);

            if (event) {
                event.preventDefault();
            }
        }
    };

    private onDeleteRow = (menuItem: IMenuItem, onDismiss: () => void) => {
        this.svc.showDialog(DialogType.Delete, menuItem.data, onDismiss);
    };

    private onRenameRow = (menuItem: IMenuItem, onDismiss: () => void) => {
        this.svc.showDialog(DialogType.Rename, menuItem.data, onDismiss);
    };

    private onGoTo = (menuItem: IMenuItem, event?: React.MouseEvent<HTMLElement, MouseEvent> | React.KeyboardEvent<HTMLElement> | undefined) => {
        onClickFPS(this.context.pageContext, menuItem.data.webUrl, true, event);
    };
}

function renderLoadingIcon(): JSX.Element {
    return <Spinner size={SpinnerSize.small} />;
}
